/**
 * Pure rating trajectory transformer - Task 1.5 / Vitest target.
 *
 * Given the chronological replay history (oldest first), calculates the
 * cumulative FTPES rating at each point. The backend stores snapshots
 * (my_score at each match), but the historical curve is derived by
 * starting from (current_score - sum_of_all_deltas) and accumulating
 * forward, or equivalently by reversing the entries (newest first from
 * the API) and walking backwards.
 *
 * This function is pure, has no side effects, and is fully unit-testable
 * without React or DOM dependencies.
 */

export interface ReplayHistoryItem {
  match_id: string;
  endgame_type: string;
  winner_id: string | null;
  played_at: string;
  my_color: 'white' | 'black';
  my_delta: number;
  my_score: number;
  opponent_id: string;
}

export interface RatingPoint {
  /** ISO timestamp of the match */
  timestamp: string;
  /** Cumulative rating AFTER this match */
  rating: number;
  /** Delta applied in this match */
  delta: number;
  /** Match ID for reference */
  matchId: string;
  /** Result label for tooltips */
  result: 'win' | 'loss' | 'draw';
}

/**
 * Calculate the historical rating trajectory from replay history.
 *
 * Algorithm:
 * 1. Sort entries by played_at ascending (oldest first).
 * 2. The earliest rating = current_score - sum_of_all_deltas (since the
 *    API returns entries with my_score being the score AFTER the match).
 *    Actually, each entry's my_score IS the rating after that match.
 *    So we can just use my_score directly from each entry, sorted chronologically.
 *    But to be safe against any API ordering, we sort by played_at and
 *    use the my_score field as the cumulative rating at that point.
 * 3. Return points with timestamp, rating, delta, matchId, and result.
 *
 * @param entries - Raw replay history entries from the API (any order)
 * @returns Chronologically sorted rating points
 */
export function calculateRatingTrajectory(
  entries: ReplayHistoryItem[],
): RatingPoint[] {
  if (entries.length === 0) return [];

  // Sort by played_at ascending (oldest first)
  const sorted = [...entries].sort(
    (a, b) => new Date(a.played_at).getTime() - new Date(b.played_at).getTime(),
  );

  const DRAW_TYPES = [
    'stalemate',
    'insufficient_material',
    'threefold_repetition',
    'fifty_move_rule',
    'fivefold_repetition',
    'seventy_five_move_rule',
    'draw_agreement',
    'draw_claim',
  ] as const;

  const isDraw = (type: string) =>
    (DRAW_TYPES as readonly string[]).includes(type);

  return sorted.map((entry) => {
    let result: 'win' | 'loss' | 'draw';
    if (isDraw(entry.endgame_type)) {
      result = 'draw';
    } else if (entry.winner_id === entry.opponent_id) {
      result = 'loss';
    } else {
      result = 'win';
    }

    return {
      timestamp: entry.played_at,
      rating: entry.my_score,
      delta: entry.my_delta,
      matchId: entry.match_id,
      result,
    };
  });
}

/**
 * Compute the overall min/max rating for axis scaling.
 */
export function getRatingExtent(points: RatingPoint[]): [number, number] {
  if (points.length === 0) return [0, 100];
  const ratings = points.map((p) => p.rating);
  const min = Math.min(...ratings);
  const max = Math.max(...ratings);
  const padding = Math.max(50, (max - min) * 0.1);
  return [min - padding, max + padding];
}

/**
 * Format a timestamp for axis labels / tooltips.
 */
export function formatRatingTime(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}